import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { InputLabel, Modal, Switch, Textarea } from '@/shared/ui';

import { templateEditorMessages } from '../../config';
import { isAiSlotName } from '../../lib';

import styles from './SlotEditorPopup.module.scss';

export type SlotEditorSaveResult = {
  values: string[];
  instructions: string;
};

type SlotEditorPopupProps = {
  isOpen: boolean;
  slotName: string;
  values: string[];
  instructions: string;
  onSave: (result: SlotEditorSaveResult) => void;
  onToggleAi: (nextIsAi: boolean) => void;
  onClose: () => void;
};

export const SlotEditorPopup: FC<SlotEditorPopupProps> = (props) => {
  const {
    isOpen,
    slotName,
    values,
    instructions,
    onSave,
    onToggleAi,
    onClose,
  } = props;

  const { formatMessage } = useIntl();

  const valuesText = values.join('\n');

  const [valuesDraft, setValuesDraft] = useState(valuesText);
  const [instructionsDraft, setInstructionsDraft] = useState(instructions);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setValuesDraft(valuesText);
    setInstructionsDraft(instructions);
    setError(undefined);
  }, [isOpen, valuesText, instructions, slotName]);

  const isAi = isAiSlotName(slotName);

  const handleSave = () => {
    if (isAi) {
      onSave({ values: [], instructions: instructionsDraft.trim() });
      return;
    }

    const parsedValues = valuesDraft
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (parsedValues.some((value) => value.includes('|'))) {
      setError(
        formatMessage(templateEditorMessages.validationSlotPipeForbidden),
      );
      return;
    }

    onSave({ values: parsedValues, instructions: instructionsDraft.trim() });
  };

  const handleValuesChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValuesDraft(event.target.value);
    setError(undefined);
  };

  const handleInstructionsChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setInstructionsDraft(event.target.value);
  };

  return (
    <Modal
      isOpen={isOpen}
      borderless
      dataComponent="slot-editor-popup"
      title={formatMessage(templateEditorMessages.editSlot)}
      okButtonText={formatMessage(templateEditorMessages.save)}
      cancelButtonText={formatMessage(templateEditorMessages.cancel)}
      onOk={handleSave}
      onCancel={onClose}
    >
      <div className={styles.body}>
        <span className={cn('p3', styles.slotName)}>{slotName}</span>

        <Switch
          checked={isAi}
          onChange={(event) => onToggleAi(event.target.checked)}
        >
          {formatMessage(templateEditorMessages.slotAiToggle)}
        </Switch>

        {isAi ? (
          <p className={cn('p3', styles.description)}>
            {formatMessage(templateEditorMessages.slotAiDescription)}
          </p>
        ) : (
          <div className={styles.field}>
            <InputLabel
              label={formatMessage(templateEditorMessages.slotValues)}
            />
            <Textarea
              value={valuesDraft}
              rows={4}
              error={error}
              placeholder={formatMessage(
                templateEditorMessages.slotValuesPlaceholder,
              )}
              onChange={handleValuesChange}
            />
          </div>
        )}

        <div className={styles.field}>
          <InputLabel
            label={formatMessage(templateEditorMessages.slotAiInstructions)}
          />
          <Textarea
            value={instructionsDraft}
            rows={3}
            placeholder={formatMessage(
              templateEditorMessages.slotAiInstructionsPlaceholder,
            )}
            onChange={handleInstructionsChange}
          />
        </div>
      </div>
    </Modal>
  );
};
